import { Landmark, ShieldCheck, Clock, Ban, CheckCircle2 } from 'lucide-react'

// QB-1 — QuickBooks Online Connection Status + Safety UI (read-only)
//
// Presentational only. This card does NOT implement OAuth, does NOT call any
// Intuit / QuickBooks Online API, does NOT handle tokens, does NOT sync or
// export data, and performs NO network requests or mutations of any kind. It
// only communicates the future, sandbox-first, manual-approval plan and the
// safety rules for a real connection.
//
// Principle: bluaIQ manages the project. QuickBooks manages the accounting.
//            bluaIQ maneja la obra. QuickBooks maneja la contabilidad.
//
// QuickBooks real sync remains BLOCKED — every action below is disabled.

interface QuickBooksStatusCardProps {
  isEs: boolean
}

// Categories bluaIQ may one day send to QuickBooks (future, not enabled).
const FUTURE_SYNC_CATEGORIES: Array<{ en: string; es: string }> = [
  { en: 'Expenses', es: 'Gastos' },
  { en: 'Bills', es: 'Cuentas por pagar (Bills)' },
  { en: 'Invoices', es: 'Facturas' },
  { en: 'Purchase Orders', es: 'Órdenes de compra' },
  { en: 'Customers', es: 'Clientes' },
  { en: 'Vendors', es: 'Proveedores' },
  { en: 'Payments (later)', es: 'Pagos (más adelante)' },
]

// Records that will always be blocked from any QuickBooks sync.
const BLOCKED_CATEGORIES: Array<{ en: string; es: string }> = [
  { en: 'Demo data', es: 'Datos de demostración' },
  { en: 'AI drafts', es: 'Borradores de IA' },
  { en: 'Ballpark estimates', es: 'Estimaciones aproximadas' },
  { en: 'Requires Review', es: 'Requiere revisión' },
  { en: 'Not source-backed', es: 'Sin respaldo de una fuente' },
  { en: 'Missing mapping', es: 'Falta asignación (mapping)' },
  { en: 'SCHEDULED payments', es: 'Pagos PROGRAMADOS (SCHEDULED)' },
]

// Every action is intentionally disabled — no OAuth, no sync, no export.
const DISABLED_BUTTONS: Array<{ en: string; es: string }> = [
  { en: 'Connect QuickBooks Online', es: 'Conectar QuickBooks Online' },
  { en: 'Review mappings', es: 'Revisar asignaciones (mappings)' },
  { en: 'Preview eligible records', es: 'Previsualizar registros elegibles' },
  { en: 'Manual sync', es: 'Sincronización manual' },
]

const SAFETY_POINTS: Array<{ en: string; es: string }> = [
  { en: 'No auto-sync', es: 'Sin sincronización automática' },
  { en: 'No production sync', es: 'Sin sincronización en producción' },
  { en: 'Sandbox first', es: 'Primero sandbox' },
  { en: 'Manual approval required', es: 'Se requiere aprobación manual' },
]

export default function QuickBooksStatusCard({ isEs }: QuickBooksStatusCardProps) {
  const disabledReason = isEs
    ? 'La sincronización con QuickBooks aún no está habilitada.'
    : 'QuickBooks sync is not enabled yet.'

  return (
    <div
      data-testid="quickbooks-status-card"
      className="bg-white border border-[#ECEAE4] rounded-xl p-4 sm:p-5 space-y-4"
    >
      {/* Header + guiding principle */}
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-lg bg-[#EFF6FF] text-[#06539B] flex items-center justify-center">
          <Landmark size={18} />
        </div>
        <div className="min-w-0">
          <div className="text-[14px] font-[900] text-[#1C1A17]">QuickBooks Online</div>
          <p className="text-[11px] font-[700] text-[#6B7280] mt-0.5 break-words">
            {isEs
              ? 'NATAMI maneja la obra. QuickBooks maneja la contabilidad.'
              : 'NATAMI manages the project. QuickBooks manages the accounting.'}
          </p>
        </div>
      </div>

      {/* Status: not connected · sandbox required · manual review required */}
      <div className="flex flex-wrap gap-2">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F3F4F6] text-[#6B7280] text-[11px] font-[800]">
          <Clock size={11} />
          {isEs ? 'No conectado' : 'Not connected'}
        </span>
        <span className="px-2.5 py-1 rounded-full bg-[#FFFBEB] text-[#92400E] text-[11px] font-[800] break-words">
          {isEs
            ? 'Se requiere sandbox antes de la sincronización en producción'
            : 'Sandbox required before production sync'}
        </span>
        <span className="px-2.5 py-1 rounded-full bg-[#FFFBEB] text-[#92400E] text-[11px] font-[800] break-words">
          {isEs ? 'Se requiere revisión manual' : 'Manual review required'}
        </span>
      </div>

      {/* Safety copy — bluaIQ only sends reviewed, source-backed records */}
      <div className="rounded-lg border border-[#DBEAFE] bg-[#F8FBFF] px-3 py-2.5 flex items-start gap-2">
        <ShieldCheck size={14} className="shrink-0 text-[#06539B] mt-0.5" />
        <p className="text-[12px] font-[700] text-[#355070] break-words">
          {isEs
            ? 'QuickBooks es tu sistema contable. NATAMI solo enviará registros revisados y respaldados por una fuente.'
            : 'QuickBooks is your accounting system. NATAMI will only send reviewed, source-backed records.'}
        </p>
      </div>

      {/* Disabled actions — read-only, nothing wired to any handler */}
      <div className="flex flex-wrap gap-2">
        {DISABLED_BUTTONS.map(btn => (
          <button
            key={btn.en}
            type="button"
            disabled
            aria-disabled="true"
            title={disabledReason}
            className="inline-flex items-center gap-1.5 bg-[#F3F4F6] text-[#9CA3AF] text-[11px] font-[800] px-3 py-2 rounded-full cursor-not-allowed"
          >
            <Clock size={12} />
            {isEs ? btn.es : btn.en}
          </button>
        ))}
      </div>
      <p className="text-[11px] font-[700] text-[#9CA3AF]">{disabledReason}</p>

      {/* Future sync categories vs. always-blocked categories — stacked on mobile */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-lg border border-[#ECEAE4] bg-[#FAFAF8] px-3 py-3">
          <div className="mb-2 flex items-center gap-1 text-[10px] font-[800] uppercase tracking-[0.3px] text-[#047857]">
            <CheckCircle2 size={12} />
            {isEs ? 'Categorías de sync futuras' : 'Future sync categories'}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {FUTURE_SYNC_CATEGORIES.map(cat => (
              <span
                key={cat.en}
                className="px-2 py-1 rounded-md bg-[#ECFDF5] text-[#047857] text-[10px] font-[700] break-words"
              >
                {isEs ? cat.es : cat.en}
              </span>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-[#ECEAE4] bg-[#FAFAF8] px-3 py-3">
          <div className="mb-2 flex items-center gap-1 text-[10px] font-[800] uppercase tracking-[0.3px] text-[#B91C1C]">
            <Ban size={12} />
            {isEs ? 'Bloqueado del sync' : 'Blocked from sync'}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {BLOCKED_CATEGORIES.map(cat => (
              <span
                key={cat.en}
                className="px-2 py-1 rounded-md bg-[#FEF2F2] text-[#B91C1C] text-[10px] font-[700] break-words"
              >
                {isEs ? cat.es : cat.en}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Clear safety points — no auto-sync · no production sync · sandbox first · manual approval */}
      <div className="flex flex-wrap gap-2">
        {SAFETY_POINTS.map(point => (
          <span
            key={point.en}
            className="px-2.5 py-1 rounded-full border border-[#ECEAE4] text-[#4B5563] text-[10px] font-[800] break-words"
          >
            {isEs ? point.es : point.en}
          </span>
        ))}
      </div>
    </div>
  )
}
